import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Building2, Plus, Trash2, RefreshCw, Users, FolderTree, Globe, AlertTriangle,
  Pencil, Link2, CheckCircle2, EyeOff, UserPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIVERGENCE_LABELS = {
  host_no_tag: 'Host sem TAG Cliente',
  tag_no_match: 'TAG sem cliente correspondente',
  orphan_group: 'Grupo de hosts órfão',
  prefix_tag_conflict: 'Divergência prefixo × TAG',
};

const emptyForm = {
  name: '', display_name: '', type: 'external', prefixes: '', domains: '',
  contacts: '', logo_url: '', is_active: true,
};

export function OrgsPage() {
  const [orgs, setOrgs] = useState([]);
  const [compliance, setCompliance] = useState({ items: [], counts: {}, total_open: 0 });
  const [hostGroups, setHostGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [userForm, setUserForm] = useState({ full_name: '', email: '', password: '', profile: 'viewer' });
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveOrg, setResolveOrg] = useState('');
  const [justification, setJustification] = useState('');
  const { getAuthHeader } = useAuthStore();

  const fetchAll = useCallback(async () => {
    try {
      const [orgsRes, compRes, groupsRes] = await Promise.all([
        axios.get(`${API}/orgs`, { headers: getAuthHeader() }),
        axios.get(`${API}/orgs/compliance`, { headers: getAuthHeader() }),
        axios.get(`${API}/hostgroups`, { headers: getAuthHeader() }).catch(() => ({ data: { groups: [] } })),
      ]);
      setOrgs(orgsRes.data);
      setCompliance(compRes.data);
      setHostGroups(groupsRes.data.groups || []);
    } catch (_) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };

  const openEdit = (org) => {
    setEditing(org);
    setForm({
      name: org.name, display_name: org.display_name || '', type: org.type || 'external',
      prefixes: (org.prefixes || []).join(', '), domains: (org.domains || []).join(', '),
      contacts: (org.contacts || []).map(c => `${c.name}|${c.email}|${c.phone || ''}`).join('\n'),
      logo_url: org.logo_url || '', is_active: org.is_active !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Informe o nome do cliente'); return; }
    const payload = {
      name: form.name.trim(),
      display_name: form.display_name.trim(),
      type: form.type,
      prefixes: form.prefixes.split(',').map(p => p.trim()).filter(Boolean),
      domains: form.domains.split(',').map(d => d.trim()).filter(Boolean),
      contacts: form.contacts.split('\n').filter(Boolean).map(line => {
        const [name, email, phone] = line.split('|');
        return { name: (name || '').trim(), email: (email || '').trim(), phone: (phone || '').trim() };
      }),
      logo_url: form.logo_url.trim(),
      is_active: form.is_active,
    };
    try {
      if (editing) {
        await axios.put(`${API}/orgs/${editing.id}`, payload, { headers: getAuthHeader() });
        toast.success('Cliente atualizado');
      } else {
        await axios.post(`${API}/orgs`, payload, { headers: getAuthHeader() });
        toast.success('Cliente criado — grupos com o prefixo foram vinculados');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar cliente');
    }
  };

  const remove = async (org) => {
    if (!window.confirm(`Excluir o cliente ${org.name}? Usuários e vínculos serão removidos.`)) return;
    await axios.delete(`${API}/orgs/${org.id}`, { headers: getAuthHeader() });
    toast.success('Cliente removido');
    fetchAll();
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/orgs/sync`, {}, { headers: getAuthHeader() });
      const detalhe = (res.data.by_org || []).map(o => `${o.name}: ${o.groups}`).join(' · ');
      const limpeza = res.data.cleanup;
      toast.success(
        `Sincronizado: ${res.data.linked || 0} vínculos em ${res.data.groups || 0} grupos, ${res.data.divergences || 0} pendências`,
        { description: detalhe || undefined, duration: 8000 }
      );
      if (limpeza && (limpeza.removed_count || limpeza.moved_count)) {
        toast.info(
          `Escopo saneado: ${limpeza.removed_count} vínculo(s) genérico(s) removido(s), ${limpeza.moved_count} movido(s) de cliente`,
          { duration: 9000 }
        );
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (org) => {
    try {
      const res = await axios.get(`${API}/orgs/${org.id}`, { headers: getAuthHeader() });
      setDetail(res.data);
    } catch (_) { toast.error('Erro ao carregar detalhes'); }
  };

  const addUser = async () => {
    if (!userForm.email || !userForm.password || !userForm.full_name) {
      toast.error('Preencha nome, email e senha'); return;
    }
    try {
      await axios.post(`${API}/orgs/${detail.id}/users`, userForm, { headers: getAuthHeader() });
      toast.success('Usuário do cliente criado — ele receberá código OTP por e-mail ao entrar');
      setUserForm({ full_name: '', email: '', password: '', profile: 'viewer' });
      openDetail(detail);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const removeUser = async (u) => {
    await axios.delete(`${API}/orgs/${detail.id}/users/${u.id}`, { headers: getAuthHeader() });
    toast.success('Usuário removido');
    openDetail(detail);
  };

  const linkGroup = async (groupId) => {
    await axios.post(`${API}/orgs/${detail.id}/groups`, { zabbix_group_id: groupId },
      { headers: getAuthHeader() });
    toast.success('Grupo vinculado manualmente (fixado)');
    openDetail(detail);
    fetchAll();
  };

  const unlinkGroup = async (groupId) => {
    await axios.delete(`${API}/orgs/${detail.id}/groups/${groupId}`, { headers: getAuthHeader() });
    toast.success('Vínculo removido');
    openDetail(detail);
  };

  const resolve = async (action) => {
    try {
      await axios.post(`${API}/orgs/compliance/${resolveTarget.id}/resolve`, {
        action,
        org_id: resolveOrg || null,
        new_org_name: resolveTarget.tag_value || resolveTarget.title,
        justification,
      }, { headers: getAuthHeader() });
      toast.success('Pendência tratada');
      setResolveTarget(null); setResolveOrg(''); setJustification('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao resolver pendência');
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-80 w-full" /></div>;
  }

  const openItems = compliance.items.filter(i => i.status === 'open');

  return (
    <div className="space-y-6" data-testid="orgs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5" /> Cliente/Org
          </h2>
          <p className="text-sm text-muted-foreground">
            Camada acima dos grupos de hosts: prefixos, domínios, usuários e conformidade
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sync} disabled={syncing} data-testid="sync-orgs-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} /> Sincronizar agora
          </Button>
          <Button onClick={openCreate} data-testid="new-org-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo Cliente
          </Button>
        </div>
      </div>

      <Tabs defaultValue="clients">
        <TabsList>
          <TabsTrigger value="clients" data-testid="tab-clients">Clientes ({orgs.length})</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">
            Conformidade
            {compliance.total_open > 0 && (
              <Badge variant="destructive" className="ml-2">{compliance.total_open}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {orgs.length === 0 ? (
                <div className="p-12 text-center">
                  <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <h3 className="font-medium">Nenhum cliente cadastrado</h3>
                  <p className="text-sm text-muted-foreground">Cadastre o primeiro cliente e informe os prefixos dos grupos de hosts</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Prefixos</TableHead>
                      <TableHead>Domínios</TableHead>
                      <TableHead className="text-center">Grupos</TableHead>
                      <TableHead className="text-center">Hosts em escopo</TableHead>
                      <TableHead className="text-center">Usuários</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgs.map(org => (
                      <TableRow key={org.id} data-testid={`org-row-${org.id}`}>
                        <TableCell>
                          <button className="font-medium hover:underline text-left" onClick={() => openDetail(org)}>
                            {org.display_name || org.name}
                          </button>
                          <div className="text-xs text-muted-foreground">{org.name}</div>
                          {(org.suspect_links || []).length > 0 && (
                            <div
                              className="mt-1 inline-flex items-center gap-1 text-xs text-amber-600"
                              title={org.suspect_links.map(s => `${s.group} — ${s.reason}`).join('\n')}
                              data-testid={`org-suspect-${org.id}`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {org.suspect_links.length} vínculo(s) suspeito(s)
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{org.type === 'internal' ? 'Área interna' : 'Cliente externo'}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{(org.prefixes || []).join(', ') || '—'}</TableCell>
                        <TableCell className="text-sm">{(org.domains || []).slice(0, 2).join(', ') || '—'}</TableCell>
                        <TableCell className="text-center">{org.groups_count}</TableCell>
                        <TableCell className="text-center" data-testid={`org-hosts-${org.id}`}>
                          <span className="font-medium">{org.hosts_in_scope ?? '—'}</span>
                          {org.hosts_shared_out > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {org.hosts_shared_out} compartilhado(s)
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">{org.users_count}</TableCell>
                        <TableCell>
                          <Badge variant={org.is_active !== false ? 'default' : 'outline'}>
                            {org.is_active !== false ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => openDetail(org)} data-testid={`detail-org-${org.id}`}>
                            <FolderTree className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(org)} data-testid={`edit-org-${org.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(org)} data-testid={`delete-org-${org.id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance" className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            {Object.entries(DIVERGENCE_LABELS).map(([key, label]) => (
              <Card key={key}>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold">{compliance.counts[key] || 0}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{label}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Pendências de padronização
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {openItems.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  Nenhuma pendência aberta. Rode &quot;Sincronizar agora&quot; para reavaliar.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Detalhe</TableHead>
                      <TableHead className="text-right">Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {openItems.slice(0, 200).map(item => (
                      <TableRow key={item.id} data-testid={`divergence-${item.id}`}>
                        <TableCell><Badge variant="outline">{DIVERGENCE_LABELS[item.type] || item.type}</Badge></TableCell>
                        <TableCell className="font-medium">{item.title}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{item.detail}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={() => setResolveTarget(item)}
                            data-testid={`resolve-${item.id}`}>
                            Resolver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create / Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[88vh]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Cliente' : 'Novo Cliente/Org'}</DialogTitle>
            <DialogDescription>
              Grupos de hosts que casam com o prefixo são vinculados automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="XIPP" data-testid="org-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Nome de exibição</Label>
                <Input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                  placeholder="XIPP Tecnologia" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="org-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="external">Cliente externo</SelectItem>
                  <SelectItem value="internal">Área interna</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prefixos dos grupos (separados por vírgula)</Label>
              <Input value={form.prefixes} onChange={e => setForm(f => ({ ...f, prefixes: e.target.value }))}
                placeholder="XIPP, LPL" data-testid="org-prefixes-input" />
              <p className="text-xs text-muted-foreground">
                XIPP casa com XIPP, XIPP/API, XIPP-DB, XIPP_Linux e XIPP Windows. Não casa com XIPPNEW.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Domínios (separados por vírgula)</Label>
              <Input value={form.domains} onChange={e => setForm(f => ({ ...f, domains: e.target.value }))}
                placeholder="xipp.com.br, app.xipp.com.br" data-testid="org-domains-input" />
              <p className="text-xs text-muted-foreground">Entram automaticamente no Web Monitor (SSL, WAF, registro).</p>
            </div>
            <div className="space-y-2">
              <Label>Contatos (um por linha: nome|email|telefone)</Label>
              <Textarea rows={3} value={form.contacts}
                onChange={e => setForm(f => ({ ...f, contacts: e.target.value }))}
                placeholder="Suporte|suporte@xipp.com.br|1199999999" />
            </div>
            <div className="space-y-2">
              <Label>Logo (URL https)</Label>
              <Input value={form.logo_url} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))}
                placeholder="https://..." />
            </div>
            <div className="flex items-center justify-between">
              <Label>Cliente ativo</Label>
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-org-btn">{editing ? 'Salvar' : 'Criar Cliente'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-3xl flex flex-col max-h-[88vh]">
          <DialogHeader>
            <DialogTitle>{detail?.display_name || detail?.name}</DialogTitle>
            <DialogDescription>
              Grupos vinculados, usuários do cliente e domínios monitorados
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <Tabs defaultValue="groups" className="flex-1 overflow-hidden flex flex-col">
              <TabsList>
                <TabsTrigger value="groups">Grupos ({detail.groups?.length || 0})</TabsTrigger>
                <TabsTrigger value="users">Usuários ({detail.users?.length || 0})</TabsTrigger>
                <TabsTrigger value="domains">Domínios ({detail.domains?.length || 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="groups" className="overflow-y-auto space-y-3">
                <div className="flex gap-2">
                  <Select onValueChange={linkGroup}>
                    <SelectTrigger className="flex-1" data-testid="link-group-select">
                      <SelectValue placeholder="Vincular grupo manualmente..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {hostGroups.filter(g => !(detail.groups || []).some(l => l.zabbix_group_id === g.id))
                        .map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Grupo</TableHead><TableHead>Origem</TableHead><TableHead className="text-right">Ações</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detail.groups || []).map(l => (
                      <TableRow key={l.id}>
                        <TableCell>{l.group_name}</TableCell>
                        <TableCell>
                          <Badge variant={l.pinned ? 'default' : 'outline'}>
                            {l.source === 'prefix' ? 'Prefixo' : l.source === 'tag' ? 'TAG Cliente' : 'Manual (fixado)'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => unlinkGroup(l.zabbix_group_id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(detail.groups || []).length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-sm text-muted-foreground text-center py-6">
                        Nenhum grupo vinculado ainda
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="users" className="overflow-y-auto space-y-3">
                <div className="grid grid-cols-4 gap-2">
                  <Input placeholder="Nome" value={userForm.full_name}
                    onChange={e => setUserForm(f => ({ ...f, full_name: e.target.value }))} data-testid="org-user-name" />
                  <Input placeholder="E-mail" value={userForm.email}
                    onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} data-testid="org-user-email" />
                  <Input placeholder="Senha" type="password" value={userForm.password}
                    onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} data-testid="org-user-password" />
                  <Button onClick={addUser} data-testid="add-org-user-btn">
                    <UserPlus className="h-4 w-4 mr-2" /> Adicionar
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Nome</TableHead><TableHead>E-mail</TableHead><TableHead>Perfil</TableHead><TableHead className="text-right">Ações</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detail.users || []).map(u => (
                      <TableRow key={u.id}>
                        <TableCell>{u.full_name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell><Badge variant="outline">{u.profile || 'viewer'}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => removeUser(u)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(detail.users || []).length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-sm text-muted-foreground text-center py-6">
                        Nenhum usuário do cliente. Eles entram pelo mesmo login e recebem código OTP por e-mail.
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="domains" className="overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {(detail.domains || []).map(d => (
                    <Badge key={d} variant="secondary" className="text-sm py-1">
                      <Globe className="h-3 w-3 mr-1" /> {d}
                    </Badge>
                  ))}
                  {(detail.domains || []).length === 0 && (
                    <p className="text-sm text-muted-foreground">Nenhum domínio cadastrado</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Resolve divergence */}
      <Dialog open={!!resolveTarget} onOpenChange={() => setResolveTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Resolver pendência</DialogTitle>
            <DialogDescription>{resolveTarget?.detail}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Vincular ao cliente</Label>
              <Select value={resolveOrg} onValueChange={setResolveOrg}>
                <SelectTrigger data-testid="resolve-org-select"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                <SelectContent>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Justificativa (para dispensar)</Label>
              <Input value={justification} onChange={e => setJustification(e.target.value)}
                placeholder="Ex: host de laboratório" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => resolve('ignore')} data-testid="ignore-divergence-btn">
              <EyeOff className="h-4 w-4 mr-2" /> Dispensar
            </Button>
            <Button variant="secondary" onClick={() => resolve('create_org')} data-testid="create-org-from-divergence-btn">
              <Plus className="h-4 w-4 mr-2" /> Criar cliente
            </Button>
            <Button onClick={() => resolve('link')} disabled={!resolveOrg} data-testid="link-divergence-btn">
              <Link2 className="h-4 w-4 mr-2" /> Vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
